import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Shield, Lock, Eye, EyeOff, User, KeyRound, Gift, Send, X, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter username, 2 = enter code
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [telegramHint, setTelegramHint] = useState("");
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const from = location.state?.from?.pathname || "/dashboard";

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      setIsRegister(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isRegister) {
        await register(username, password, null, referralCode || null);
        navigate(from, { replace: true });
      } else {
        await login(username, password, totpCode || null);
        navigate(from, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Authentication failed";
      if (message === "2FA code required") {
        setRequires2FA(true);
        setError("");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password handlers
  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");
    setForgotLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/forgot-password/request`, {
        username: forgotUsername
      });
      setTelegramHint(response.data.telegram_hint || "");
      setForgotSuccess("Verification code sent to your Telegram!");
      setForgotStep(2);
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Failed to send verification code");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotPasswordVerify = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");
    setForgotLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/forgot-password/verify`, {
        username: forgotUsername,
        code: forgotCode
      });
      setForgotSuccess(response.data.message || "Password reset successful!");
      // Auto close after 3 seconds
      setTimeout(() => {
        closeForgotPassword();
      }, 3000);
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Failed to verify code");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotUsername("");
    setForgotCode("");
    setForgotError("");
    setForgotSuccess("");
    setTelegramHint("");
  };

  const backgroundImage = "https://customer-assets.emergentagent.com/job_4db224ed-4d06-4a2b-9ec3-96b2cd273fc3/artifacts/6ia4l2ja_1.jpg";

  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Dark overlay */}
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-[2px]"></div>
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-sm">
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-xl p-6 sm:p-8 shadow-2xl border border-slate-700/50">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <Logo size="lg" showText={false} testId="login-logo" />
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-orbitron font-bold text-center mb-1 text-white" data-testid="login-title">
            WIZARD X
          </h1>
          <p className="text-cyan-400/70 text-center mb-6 text-sm tracking-widest uppercase">
            {isRegister ? "CREATE YOUR ACCOUNT" : "SECURE ACCESS PORTAL"}
          </p>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-3 text-sm transition-all"
                required
                data-testid="login-username"
              />
            </div>

            {/* Referral Code (Register only) */}
            {isRegister && (
              <div className="relative">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Referral Code (optional)"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-3 text-sm transition-all uppercase"
                  maxLength={8}
                  data-testid="login-referral"
                />
                {referralCode && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-400" data-testid="login-referral-applied">Referral Applied</span>
                )}
              </div>
            )}

            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-10 text-sm transition-all"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                data-testid="login-password-visibility-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Forgot Password Link (Login only) */}
            {!isRegister && !requires2FA && (
              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-cyan-400/70 hover:text-cyan-400 text-xs transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {/* 2FA Code */}
            {requires2FA && !isRegister && (
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="2FA Code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-3 text-sm transition-all"
                  maxLength={6}
                  pattern="[0-9]*"
                  data-testid="login-2fa"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-lg shadow-lg shadow-cyan-500/20 transition-all hover:shadow-cyan-500/30 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isRegister ? "Creating..." : "Signing in..."}
                </span>
              ) : (
                <span>{isRegister ? "Create Account" : "Login"}</span>
              )}
            </button>
          </form>

          {/* Toggle Register/Login */}
          <div className="mt-5 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setError("");
                setRequires2FA(false);
              }}
              className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
              data-testid="login-toggle"
            >
              {isRegister ? "Already have an account? Login" : "Need an account? Register"}
            </button>
          </div>

          {/* Security Badge */}
          <div className="mt-5 flex items-center justify-center gap-2 text-slate-500 text-xs">
            <Shield className="w-3 h-3" />
            <span>Protected by Deeper Network Security</span>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeForgotPassword} data-testid="forgot-password-backdrop"></div>
          <div className="relative bg-slate-900/95 backdrop-blur-xl rounded-xl p-6 w-full max-w-sm border border-slate-700/50 shadow-2xl" data-testid="forgot-password-modal">
            {/* Close button */}
            <button
              onClick={closeForgotPassword}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
              data-testid="forgot-password-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Send className="w-6 h-6 text-cyan-400" />
              </div>
              <h2 className="text-xl font-semibold text-white" data-testid="forgot-password-title">Reset Password</h2>
              <p className="text-slate-400 text-sm mt-1" data-testid="forgot-password-instructions">
                {forgotStep === 1 
                  ? "Enter your username to receive a reset code via Telegram" 
                  : `Enter the code sent to Telegram ${telegramHint}`}
              </p>
            </div>

            {/* Error/Success Messages */}
            {forgotError && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm" data-testid="forgot-password-error">
                {forgotError}
              </div>
            )}
            {forgotSuccess && (
              <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-3 py-2 rounded-lg mb-4 text-sm" data-testid="forgot-password-success">
                {forgotSuccess}
              </div>
            )}

            {/* Step 1: Enter Username */}
            {forgotStep === 1 && (
              <form onSubmit={handleForgotPasswordRequest} className="space-y-4">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={forgotUsername}
                    onChange={(e) => setForgotUsername(e.target.value)}
                    className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-3 text-sm transition-all"
                    required
                    autoFocus
                    data-testid="forgot-password-username"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading || !forgotUsername}
                  className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-lg shadow-lg shadow-cyan-500/20 transition-all hover:shadow-cyan-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="forgot-password-request-btn"
                >
                  {forgotLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Reset Code</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Step 2: Enter Verification Code */}
            {forgotStep === 2 && (
              <form onSubmit={handleForgotPasswordVerify} className="space-y-4">
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-slate-800/80 border border-slate-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder-slate-500 rounded-lg h-11 pl-10 pr-3 text-sm transition-all text-center tracking-widest font-mono"
                    required
                    maxLength={6}
                    autoFocus
                    data-testid="forgot-password-code"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading || forgotCode.length !== 6}
                  className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-lg shadow-lg shadow-cyan-500/20 transition-all hover:shadow-cyan-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="forgot-password-verify-btn"
                >
                  {forgotLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <span>Verify & Reset Password</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setForgotStep(1); setForgotCode(""); setForgotError(""); setForgotSuccess(""); }}
                  className="w-full text-slate-400 hover:text-white text-sm flex items-center justify-center gap-1 transition-colors"
                  data-testid="forgot-password-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to username
                </button>
              </form>
            )}

            {/* Info text */}
            <p className="text-slate-500 text-xs text-center mt-4">
              A new password will be sent to your linked Telegram account
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
